import { privateClient } from "@/api/privateClient";
import { SUPER_ADMIN_ENDPOINTS } from "../../endpoint";
import { fetchOrgTree } from "./orgTreeService";
import type { Organization, RegisterOrganizationPayload, RegisterOrganizationResult } from "../types";
import type { ApiOrganization } from "../types";

export const listOrganizations = async (): Promise<Organization[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.LIST);
  return data.organizations;
};

export const registerOrganization = async (
  payload: RegisterOrganizationPayload
): Promise<RegisterOrganizationResult> => {
  const { data } = await privateClient.post(SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.REGISTER, payload);
  return data;
};

export const resetOrganizationAdminPassword = async (userId: number | string) => {
  const { data } = await privateClient.post(`/users/${userId}/reset-password`);
  return data;
};

export const updateOrganization = async (
  id: number,
  payload: Record<string, unknown>
): Promise<{ organization: ApiOrganization }> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.UPDATE(String(id)), payload);
  return data;
};

// ---------------------------------------------------------------------
// Single-organization detail fetch, for OrganizationDetailsPage.tsx.
//
// Two calls are still needed because GET /organizations/{id} doesn't
// return the campus/institution tree (that's only ever assembled by
// GET /dashboard/super-admin/hierarchy). They're fired in PARALLEL via
// Promise.allSettled rather than sequentially.
//
// GET /organizations/{id} used to return raw snake_case DB columns
// instead of the camelCase shape POST/PUT return — logoUrl/primaryColor/
// etc. (and isActive/createdAt, missing entirely) were silently always
// undefined from this call, invisibly patched over by the tree fallback
// below. Fixed at the source (see backend organizationService.js's
// toApiShape) — `base` is now authoritative for every field except
// campuses/institutionsDirect, which still only come from the tree.
// ---------------------------------------------------------------------

interface RawOrganizationResponse {
  organization: {
    id: number;
    name: string;
    code: string;
    email: string;
    phone?: string;
    whatsappNumber?: string;
    adminEmail?: string;
    orgStartDate?: string;
    notificationEmail?: string;
    address?: string;
    city?: string;
    state?: string;
    district?: string;
    pincode?: string;
    landmark?: string;
    website?: string;
    leadSource?: string;
    logoUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    accentColor?: string | null;
    backgroundColor?: string | null;
    isActive: boolean;
    createdAt: string;
    adminUserId?: number | null;
  };
}

export async function getOrganizationDetails(id: number): Promise<ApiOrganization> {
  // Fire both requests at once instead of awaiting one after the other.
  const [detailResult, treeResult] = await Promise.allSettled([
    privateClient.get<RawOrganizationResponse>(
      SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.DETAIL(String(id))
    ),
    fetchOrgTree({ page: 1, limit: 1, organizationId: id, includeIndependent: false }),
  ]);

  if (detailResult.status === "rejected") {
    // The contact-info call is the one that must succeed — if it
    // fails, there's nothing to show, so propagate the error the same
    // way the old sequential version did.
    throw detailResult.reason;
  }

  const base = detailResult.value.data.organization;

  // Only used for campuses/institutionsDirect now — everything else
  // comes straight from `base`. If the tree call failed, don't fail the
  // whole page, just show an empty structure until it can be retried.
  const treeMatch =
    treeResult.status === "fulfilled"
      ? treeResult.value.organizations.find((o) => o.id === id)
      : undefined;

  return {
    id: base.id,
    name: base.name,
    code: base.code,
    email: base.email,
    phone: base.phone,
    whatsappNumber: base.whatsappNumber,
    orgStartDate: base.orgStartDate,
    adminEmail: base.adminEmail,
    address: base.address,
    city: base.city,
    state: base.state,
    district: base.district,
    pincode: base.pincode,
    landmark: base.landmark,
    website: base.website,
    leadSource: base.leadSource,
    logoUrl: base.logoUrl ?? null,
    baseColor: treeMatch?.baseColor ?? null,
    primaryColor: base.primaryColor ?? null,
    secondaryColor: base.secondaryColor ?? null,
    accentColor: base.accentColor ?? null,
    backgroundColor: base.backgroundColor ?? null,
    isActive: base.isActive,
    createdAt: base.createdAt,
    adminUserId: base.adminUserId ?? null,
    campuses: treeMatch?.campuses ?? [],
    institutionsDirect: treeMatch?.institutionsDirect ?? [],
  };
}
export const getOrgTemplateGrants = async (organizationId: number): Promise<number[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.ACADEMIC_TEMPLATES(String(organizationId)));
  return data.templateIds ?? [];
};

export const setOrgTemplateGrants = async (organizationId: number, templateIds: number[]): Promise<number[]> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.ORGANIZATIONS.ACADEMIC_TEMPLATES(String(organizationId)), { templateIds });
  return data.templateIds ?? [];
};
